// =====================================================================
// HIJRI DATEPICKER — dual Gregorian + Hijri on EVERY Date field
// =====================================================================
// Frappe v16 date fields use air-datepicker v2 (frappe fork). Its
// `onRenderCell(date, cellType)` may return `{ html, classes, disabled }`
// to fully customise a cell. We compose (never replace) Frappe's own
// onRenderCell so each day shows the Gregorian number (big) with the
// Umm-al-Qura Hijri number (small) underneath — exactly like the
// reference screenshot — and the nav header gets the Hijri month/year.
//
// SAFE: air-datepicker is body-appended & absolutely positioned; this
// only augments its cell HTML + header text. It cannot touch / trap the
// Child Table grid. One-time prototype wrap, fully defensive.
// =====================================================================

function lang() {
	return document.documentElement.getAttribute("lang") === "ar" ? "ar" : "en";
}

let _hd = null, _hm = null, _hmo = null, _hy = null;
function hijriDay(d) {
	try {
		if (!_hd) _hd = new Intl.DateTimeFormat("en-US-u-ca-islamic-umalqura", { day: "numeric" });
		return _hd.format(d);
	} catch (e) { return ""; }
}
function hijriMonthName(d) {
	try {
		const loc = (lang() === "ar" ? "ar-SA" : "en-US") + "-u-ca-islamic-umalqura";
		if (!_hmo || _hmo._loc !== loc) { _hmo = new Intl.DateTimeFormat(loc, { month: "long" }); _hmo._loc = loc; }
		return _hmo.format(d);
	} catch (e) { return ""; }
}
function hijriYear(d) {
	try {
		if (!_hy) _hy = new Intl.DateTimeFormat("en-US-u-ca-islamic-umalqura", { year: "numeric" });
		// a Gregorian year straddles two Hijri years — show the span
		const a = _hy.format(new Date(d.getFullYear(), 0, 15));
		const b = _hy.format(new Date(d.getFullYear(), 11, 15));
		return a === b ? a : a + "–" + b;
	} catch (e) { return ""; }
}
function hijriMonthYear(d) {
	try {
		const loc = (lang() === "ar" ? "ar-SA" : "en-US") + "-u-ca-islamic-umalqura";
		if (!_hm || _hm._loc !== loc) { _hm = new Intl.DateTimeFormat(loc, { month: "long", year: "numeric" }); _hm._loc = loc; }
		// Intl already includes the era (هـ / AH) — do NOT append again.
		return _hm.format(d);
	} catch (e) { return ""; }
}

function injectHeaderHijri(dp) {
	try {
		const $dp = dp && dp.$datepicker;
		if (!$dp) return;
		const navTitle = $dp[0].querySelector(".datepicker--nav-title");
		if (!navTitle) return;
		// derive the displayed month from air-datepicker state
		const vd = dp.currentDate || (dp.parsedDate && dp.parsedDate.date) || new Date();
		const base = new Date(vd.getFullYear ? vd.getFullYear() : new Date().getFullYear(),
			vd.getMonth ? vd.getMonth() : 0, 15);
		let sub = navTitle.querySelector(".e16-dp-hijri");
		if (!sub) {
			sub = document.createElement("span");
			sub.className = "e16-dp-hijri";
			navTitle.appendChild(sub);
		}
		sub.textContent = hijriMonthYear(base);
	} catch (e) {}
}

let patched = false;
function patch() {
	if (patched) return;
	if (!(window.frappe && frappe.ui && frappe.ui.form && frappe.ui.form.ControlDate)) return;
	const proto = frappe.ui.form.ControlDate.prototype;
	if (proto.__e16_hijri) return;

	const origOpts = proto.set_date_options;
	proto.set_date_options = function (...a) {
		const out = origOpts.apply(this, a);
		try {
			const o = this.datepicker_options || {};
			const prevRender = o.onRenderCell;
			o.onRenderCell = (date, cellType) => {
				let base;
				try { base = prevRender ? prevRender(date, cellType) : undefined; } catch (e) { base = undefined; }
				if (date instanceof Date) {
					let g = "", h = "";
					if (cellType === "day") {
						g = date.getDate();
						h = hijriDay(date);
					} else if (cellType === "month") {
						g = new Intl.DateTimeFormat(
							lang() === "ar" ? "ar" : "en", { month: "short" }
						).format(date);
						h = hijriMonthName(date);
					} else if (cellType === "year") {
						g = date.getFullYear();
						h = hijriYear(date);
					}
					if (g !== "") {
						const html =
							'<span class="e16-dc e16-dc--' + cellType + '">' +
							'<span class="e16-dc-g">' + g + "</span>" +
							(h ? '<span class="e16-dc-h">' + h + "</span>" : "") +
							"</span>";
						return base && typeof base === "object" ? { ...base, html } : { html };
					}
				}
				return base;
			};
			const prevShow = o.onShow;
			o.onShow = (...sa) => {
				try { if (prevShow) prevShow.apply(this, sa); } catch (e) {}
				const self = this;
				setTimeout(() => { try { injectHeaderHijri(self.datepicker); } catch (e) {} }, 0);
			};
			// keep header in sync when navigating months
			const prevNav = o.onChangeMonth;
			o.onChangeMonth = (...na) => {
				try { if (prevNav) prevNav.apply(this, na); } catch (e) {}
				const self = this;
				setTimeout(() => { try { injectHeaderHijri(self.datepicker); } catch (e) {} }, 0);
			};
		} catch (e) {
			console.warn("[e16] hijri-datepicker", e);
		}
		return out;
	};
	// ---- 2. DUAL VALUE READOUT ---------------------------------------
	// After Frappe sets the field value, mirror it as a small Hijri
	// caption next to the input. The input itself stays Gregorian (so
	// form save / DB storage are untouched) — the user just SEES both
	// dates as one value. Safe: only injects a sibling <small> in the
	// field's .control-input, never touches the grid or any ancestor.
	const origSFI = proto.set_formatted_input;
	proto.set_formatted_input = function (...a) {
		const out = origSFI.apply(this, a);
		try { setTimeout(() => updateHijriCaption(this), 0); } catch (e) {}
		return out;
	};
	const origRI = proto.refresh_input;
	if (typeof origRI === "function") {
		proto.refresh_input = function (...a) {
			const out = origRI.apply(this, a);
			try { setTimeout(() => updateHijriCaption(this), 0); } catch (e) {}
			return out;
		};
	}

	proto.__e16_hijri = true;
	patched = true;
}

// ---------------------------------------------------------------------
// updateHijriCaption — sibling <small> next to the date input/value
// (defined OUTSIDE patch() so it's reused by every patched control)
// ---------------------------------------------------------------------
function getDateValueAsDate(ctrl) {
	try {
		if (ctrl.datepicker && ctrl.datepicker.selectedDates && ctrl.datepicker.selectedDates[0]) {
			return ctrl.datepicker.selectedDates[0];
		}
		const v = ctrl.get_value && ctrl.get_value();
		if (!v) return null;
		if (window.frappe && frappe.datetime && frappe.datetime.str_to_obj) {
			const d = frappe.datetime.str_to_obj(v);
			if (d && !isNaN(d.getTime())) return d;
		}
		let m = String(v).match(/^(\d{4})-(\d{2})-(\d{2})/);
		if (m) return new Date(+m[1], +m[2] - 1, +m[3]);
		m = String(v).match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{4})/);
		if (m) return new Date(+m[3], +m[2] - 1, +m[1]);
	} catch (e) {}
	return null;
}

function fmtHijriFull(d) {
	try {
		const loc = (lang() === "ar" ? "ar-SA" : "en-US") + "-u-ca-islamic-umalqura";
		return new Intl.DateTimeFormat(loc, {
			day: "numeric", month: "long", year: "numeric"
		}).format(d);
	} catch (e) { return ""; }
}

function updateHijriCaption(ctrl) {
	try {
		if (!ctrl || !ctrl.$wrapper || !ctrl.$wrapper[0]) return;
		if (ctrl.df && ctrl.df.fieldtype !== "Date" && ctrl.df.fieldtype !== "Datetime") return;
		const wrap = ctrl.$wrapper[0];
		const ci = wrap.querySelector(".control-input");
		const cv = wrap.querySelector(".control-value");
		const host = (ci && ci.offsetParent !== null) ? ci : (cv || ci);
		if (!host) return;
		let cap = host.querySelector(":scope > .e16-hijri-value");
		if (!cap) {
			cap = document.createElement("small");
			cap.className = "e16-hijri-value";
			host.appendChild(cap);
		}
		const d = getDateValueAsDate(ctrl);
		if (!d) { cap.textContent = ""; cap.style.display = "none"; return; }
		const text = fmtHijriFull(d);
		cap.textContent = text || "";
		cap.style.display = text ? "" : "none";
	} catch (e) { /* never throw into Frappe */ }
}

export const hijriDatepicker = {
	mount() {
		if (window.frappe && frappe.ui && frappe.ui.form && frappe.ui.form.ControlDate) {
			patch();
		} else {
			let n = 0;
			const iv = setInterval(() => {
				n++;
				if (window.frappe && frappe.ui && frappe.ui.form && frappe.ui.form.ControlDate) {
					patch();
					clearInterval(iv);
				} else if (n > 80) {
					clearInterval(iv);
				}
			}, 250);
		}
	},
};
